<template>
  <ul class="dca-legend" data-testid="dca-allocation-legend">
    <li
      v-for="agency in agencies"
      :key="agency.dca"
      class="dca-legend__item text-caption"
      :data-testid="`dca-legend-${agency.dca}`"
    >
      <span
        class="dca-legend__swatch"
        :style="{ backgroundColor: agency.colour }"
        aria-hidden="true"
      />
      {{ agency.name }}
    </li>
  </ul>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { AGENCY_COLOURS, agencyColour, agencyName } from "../dcaAgencyColours"

  /**
   * Which colour is which agency.
   *
   * Each cell already prints its split in words underneath the bar, so this is
   * reinforcement rather than the only cue - but the bars are what the eye reads first
   * when scanning across segments, and two of the brand colours sit at the extremes of
   * the lightness range. Naming them once removes the guesswork.
   *
   * Moriarty is listed even though it is never an allocatable agency, because it appears
   * in the matrix as the litigation gate and its colour needs the same explanation. The
   * Empty sentinel is not an agency and is excluded.
   */
  export default defineComponent({
    computed: {
      agencies(): { dca: string; name: string; colour: string }[] {
        return Object.keys(AGENCY_COLOURS)
          .filter((dca) => dca !== "Empty")
          .map((dca) => ({
            dca,
            name: agencyName(dca),
            colour: agencyColour(dca),
          }))
      },
    },
  })
</script>

<style scoped>
  .dca-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    padding: 0;
    margin: 0 0 12px;
    list-style: none;
  }

  .dca-legend__item {
    display: flex;
    align-items: center;
    gap: 6px;
    color: rgb(var(--v-theme-on-surface));
  }

  .dca-legend__swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    /* Same hairline as the bar segments, so the extreme colours have an edge. */
    box-shadow: inset 0 0 0 1px rgba(var(--v-theme-on-surface), 0.25);
  }
</style>
